import React, { useRef, useState } from 'react';
import Footer from '../components/Footer';
import { supabase } from '../supabase';
import './Contact.css';

const WIXBASE = 'https://static.wixstatic.com/media/';

function FadeUp({ children, delay = 0, className = '' }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  React.useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`fade-up ${visible ? 'fade-up--visible' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

export default function Contact({ navigate, showToast }) {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.first_name.trim()) e.first_name = 'Required';
    if (!form.email.trim()) e.email = 'Required';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Invalid email';
    if (!form.message.trim()) e.message = 'Required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    const { error } = await supabase.from('contact_messages').insert({
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      message: form.message,
    });
    setLoading(false);
    if (error) {
      showToast('Something went wrong. Please try again.', 'error');
    } else {
      setSent(true);
      showToast("Message sent! We'll be in touch soon.", 'success');
      setForm({ first_name: '', last_name: '', email: '', message: '' });
    }
  };

  const infoItems = [
    { icon: '✉', label: 'Email', value: 'shareyoursound01@gmail.com', href: 'mailto:shareyoursound01@gmail.com' },
    { icon: '🕐', label: 'Hours', value: 'Mon-Fri, 8:00 AM to 5:00 PM EST' },
    { icon: '💬', label: 'Discord', value: 'Join our community', href: 'https://discord.com/invite/AMQEeW445H' },
  ];

  return (
    <div className="contact-page">
      <div className="contact-layout">
        <div className="contact-left">
          <div className="contact-left-bg">
            <img src={WIXBASE + 'nsplsh_c503ccc4336b491495ad05f1dd55014f~mv2.jpg'} alt="" className="contact-bg-img" />
            <div className="contact-bg-overlay" />
          </div>
          <div className="contact-left-content">
            <div className="contact-ghost-text">Get<br/>In<br/>Touch</div>
            <div className="section-tag">Reach Out</div>
            <h1 className="contact-h1">We're here<br/>to <em>help.</em></h1>
            <p>We'd love to hear from you. We usually reply within a couple of business days.</p>
            <div className="contact-info-list">
              {infoItems.map((item, i) => (
                <div key={i} className="contact-info-item">
                  <span className="contact-info-icon">{item.icon}</span>
                  <div>
                    <div className="contact-info-label">{item.label}</div>
                    {item.href
                      ? <a href={item.href} target={item.href.startsWith('http') ? '_blank' : undefined} rel="noopener noreferrer" className="contact-info-value contact-info-link">{item.value}</a>
                      : <div className="contact-info-value">{item.value}</div>
                    }
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="contact-right">
          <FadeUp>
            <h2 className="contact-form-title">Send us a message</h2>
            <p className="contact-form-sub">We'd love to hear from you.</p>
          </FadeUp>

          {sent ? (
            <FadeUp delay={100} className="contact-success">
              <div className="contact-success-icon">✓</div>
              <h3>Message Received!</h3>
              <p>Thanks for reaching out! We'll get back to you soon.</p>
              <button className="btn-outline-dark" onClick={() => setSent(false)} style={{ marginTop: '1.5rem' }}>
                Send Another
              </button>
            </FadeUp>
          ) : (
            <FadeUp delay={100}>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-row-2">
                  <div className="form-group">
                    <label>First Name *</label>
                    <input type="text" placeholder="Jane" value={form.first_name} onChange={e => set('first_name', e.target.value)} />
                    {errors.first_name && <span className="form-err">{errors.first_name}</span>}
                  </div>
                  <div className="form-group">
                    <label>Last Name</label>
                    <input type="text" placeholder="Smith" value={form.last_name} onChange={e => set('last_name', e.target.value)} />
                  </div>
                </div>
                <div className="form-group">
                  <label>Email Address *</label>
                  <input type="email" placeholder="jane@example.com" value={form.email} onChange={e => set('email', e.target.value)} />
                  {errors.email && <span className="form-err">{errors.email}</span>}
                </div>
                <div className="form-group">
                  <label>Message *</label>
                  <textarea placeholder="Tell us how we can help..." value={form.message} onChange={e => set('message', e.target.value)} style={{ minHeight: '150px' }} />
                  {errors.message && <span className="form-err">{errors.message}</span>}
                </div>
                <button type="submit" className="btn-primary contact-submit" disabled={loading}>
                  {loading ? <span className="contact-spinner" /> : null}
                  {loading ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </FadeUp>
          )}
        </div>
      </div>

      <Footer navigate={navigate} />
    </div>
  );
}
